import { useCallback, useEffect, useRef, useState } from 'react';
import { QueryStatus } from '@reduxjs/toolkit/query';

type MutationOutcome = { data?: unknown; error?: unknown };
export type MutationHandle = PromiseLike<MutationOutcome> & { reset?: () => void; requestId?: string };
type MutationState = MutationOutcome & { reset?: () => void; originalArgs?: unknown };
type Lifecycle<State extends MutationState> = {
  status: QueryStatus;
  isUninitialized: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  data?: State['data'];
  error?: State['error'];
  originalArgs?: State['originalArgs'];
  requestId?: string;
  startedTimeStamp?: number;
  fulfilledTimeStamp?: number;
};
const empty = {
  status: QueryStatus.uninitialized, isUninitialized: true, isLoading: false, isSuccess: false, isError: false,
  data: undefined, error: undefined, originalArgs: undefined, requestId: undefined,
  startedTimeStamp: undefined, fulfilledTimeStamp: undefined,
};

// Access invalidation may remove RTK's mutation entry while its promise is still
// live. Observe that promise locally; never retain the invalidated query cache.
export const useMutationLifecycle = <State extends MutationState>(state: State) => {
  const [lifecycle, setLifecycle] = useState<Lifecycle<State>>(empty);
  const active = useRef<object>();
  const mounted = useRef(true);
  const originalReset = useRef(state.reset);
  originalReset.current = state.reset;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; active.current = undefined; };
  }, []);

  const forget = useCallback(() => {
    active.current = undefined;
    if (mounted.current) setLifecycle(empty);
  }, []);
  const reset = useCallback(() => {
    forget();
    originalReset.current?.();
  }, [forget]);

  const observe = useCallback(<Result extends MutationHandle>(result: Result, args: unknown) => {
    const token = {};
    active.current = token;
    const pending: Lifecycle<State> = {
      ...empty, status: QueryStatus.pending, isUninitialized: false, isLoading: true,
      requestId: result.requestId, originalArgs: args, startedTimeStamp: Date.now(),
    };
    if (mounted.current) setLifecycle(pending);
    const finish = (value: MutationOutcome) => {
      if (!mounted.current || active.current !== token) return;
      const failed = value.error !== undefined;
      setLifecycle({
        ...pending, status: failed ? QueryStatus.rejected : QueryStatus.fulfilled,
        isLoading: false, isSuccess: !failed, isError: failed,
        data: value.data, error: value.error, fulfilledTimeStamp: failed ? undefined : Date.now(),
      });
    };
    const promise = Promise.resolve(result).then(value => {
      finish(value);
      return value;
    }, error => {
      finish({ error });
      throw error;
    });
    // Do not replace reset on RTK's own handle: its hook calls that method during
    // cleanup of the previous request. Only an explicit caller reset forgets ours.
    return Object.assign(promise, result, { reset: () => {
      if (active.current === token) forget();
      result.reset?.();
    } });
  }, [forget]);

  return [observe, { ...state, ...lifecycle, reset }] as const;
};
